import { Component } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ClassTransformerHelper } from '../../../../../helpers/class.transformer.helper';
import { WorkflowType } from '../../../../../models/enums/workflowtype.enum';
import { WorkflowExitInfo, WorkflowModuleSettingsHelper, WorkflowRegistry } from '../../../../../services/workflow.service';
import { DataCheck, WorkflowDialogContent } from '../../../../../workflow/workflow.dialog';
import { MultiFeatureModelSettingsData } from '../multifeaturemodel/multi.feature.model.settings';

@Component({
    selector: 'wf-multi-feature-prediction-settings',
    templateUrl: './multi.feature.prediction.settings.html',
    styleUrls: ['./multi.feature.prediction.settings.css']
})
export class MultiFeaturePredictionSettings extends WorkflowDialogContent {
    Data = new MultiFeaturePredictionSettingsData();
    Columns = [];
    HasDateCol = false;
    SubGroupingDisabled = true;

    static GetRegistry(): WorkflowRegistry {
        const reg = new WorkflowRegistry();
        reg.ID = 'multiFeaturePredictionWFModule';
        reg.Caption = '@@Multi feature prediction';
        reg.GroupID = 'aioperations';
        reg.Index = 230;
        reg.SettingsControl = MultiFeaturePredictionSettings;
        reg.SettingsTypeHelper = new MultiFeaturePredictionSettingsDataHelper();
        reg.WorkflowType = WorkflowType.Service;
        return reg;
    }

    constructor(private translate: TranslateService) {
        super();
        this.UseActualState = true;
        this.HasExpressions = true;
    }

    initialize(data: any) {
        if (data) {
            this.Data = ClassTransformerHelper.CopyObject(data, MultiFeaturePredictionSettingsData);
        }
        const dataSource = this.ActualState.get('DataSource');
        if (dataSource) {
            const list = [];
            let groupCol, subGroupCol;
            dataSource.Columns.forEach(col => {
                const val = {
                    Name: col.Name,
                    DateCol: col.Name === this.Data.DateColumn,
                    ValCol: col.Name === this.Data.ValueColumn,
                    CatCol: this.Data.CatColumns && this.Data.CatColumns.some(cc => cc === col.Name),
                    Grouping: false,
                    SubGrouping: false
                };
                if (this.Data.Grouping && col.Name === this.Data.Grouping) {
                    groupCol = val;
                }
                if (this.Data.SubGrouping && col.Name === this.Data.SubGrouping) {
                    subGroupCol = val;
                }
                if (val.DateCol) {
                    this.HasDateCol = true;
                }
                list.push(val);
            });
            if (groupCol && groupCol.CatCol) {
                groupCol.Grouping = true;
                this.SubGroupingDisabled = false;
                if (subGroupCol && subGroupCol.CatCol && !subGroupCol.Grouping) {
                    subGroupCol.SubGrouping = true;
                }
            }
            this.Columns = list;
        }
    }

    getResult() {
        let dateCol, valCol, groupCol, subGroupCol;
        const catCols = [];
        this.Columns.forEach(c => {
            if (c.DateCol) {
                dateCol = c.Name;
            }
            if (c.ValCol) {
                valCol = c.Name;
            }
            if (c.CatCol) {
                catCols.push(c.Name);
                if (c.Grouping) {
                    groupCol = c.Name;
                } else if (c.SubGrouping) {
                    subGroupCol = c.Name;
                }
            }
        });
        this.Data.DateColumn = dateCol;
        this.Data.ValueColumn = valCol;
        this.Data.CatColumns = catCols;
        this.Data.Grouping = groupCol;
        if (groupCol && subGroupCol) {
            this.Data.SubGrouping = subGroupCol;
        } else {
            this.Data.SubGrouping = null;
        }
        if (!this.Data.DateColumn) {
            this.Data.Augments = 0;
            this.Data.Rounding = null;
        }
        return this.Data;
    }

    checkData(): DataCheck {
        const retVal = new DataCheck();
        if (this.Columns.length > 0) {
            let hasDateCol = false;
            let hasValCol = false;
            this.Columns.some(x => {
                if (x.DateCol) {
                    hasDateCol = true;
                }
                if (x.ValCol) {
                    hasValCol = true;
                }
                return hasDateCol && hasValCol;
            });
            if (!hasDateCol || !hasValCol || !this.Data.ModelKey) {
                retVal.IsCorrect = false;
                retVal.Error = '@@Bitte setzen Sie Datumsspalte, Wertspalte und ModelKey';
            }
        }
        return retVal;
    }

    GetExpressionProperties() {
        return [
            {
                Caption: this.translate.instant('@@Datumsspalte'),
                Value: 'DateColumn'
            },
            {
                Caption: this.translate.instant('@@Wertspalte'),
                Value: 'ValueColumn'
            },
            {
                Caption: this.translate.instant('@@Datums-Ebene'),
                Value: 'Rounding'
            },
            {
                Caption: this.translate.instant('@@Zusaetzliche Spalten'),
                Value: 'Augments'
            },
            {
                Caption: this.translate.instant('@@Datenzeitraum in Tagen'),
                Value: 'Days'
            },
            {
                Caption: this.translate.instant('@@Vorhersagezeitraum in Tagen'),
                Value: 'DaysOut'
            },
            {
                Caption: this.translate.instant('@@Steps verwenden'),
                Value: 'UseSteps'
            },
            {
                Caption: this.translate.instant('@@Steps'),
                Value: 'Steps'
            },
            {
                Caption: this.translate.instant('@@Kategorie'),
                Value: 'CatColumns'
            },
            {
                Caption: this.translate.instant('@@Gruppierung'),
                Value: 'Grouping'
            },
            {
                Caption: this.translate.instant('@@Untergruppierung'),
                Value: 'SubGrouping'
            },
            {
                Caption: this.translate.instant('@@ModelEstimators'),
                Value: 'ModelEstimators'
            },
            {
                Caption: this.translate.instant('@@ModelDepth'),
                Value: 'ModelDepth'
            },
            {
                Caption: this.translate.instant('@@Land'),
                Value: 'RegionSetting.Country'
            },
            {
                Caption: this.translate.instant('@@Region'),
                Value: 'RegionSetting.Region'
            }
        ];
    }

    stepsChanged(ev) {
        if (ev && ev.value && this.Data) {
            this.Data.Steps = ev.value;
        }
    }

    dateColChanged(ev, col) {
        if (ev && ev.checked) {
            this.Columns.forEach(c => c.DateCol = false);
            col.DateCol = true;
            this.HasDateCol = true;
        } else {
            this.HasDateCol = false;
        }
    }

    valColChanged(ev, col) {
        if (ev && ev.checked) {
            this.Columns.forEach(c => c.ValCol = false);
            col.ValCol = true;
        }
    }

    catColChanged(ev, col) {
        if (!ev || !ev.checked) {
            if (col.Grouping) {
                col.Grouping = false;
                this.Columns.forEach(c => c.SubGrouping = false);
                this.SubGroupingDisabled = true;
            }
            if (col.SubGrouping) {
                col.SubGrouping = false;
            }
        }
    }

    groupingChanged(ev, col) {
        if (ev && ev.checked) {
            this.Columns.forEach(c => c.Grouping = false);
            col.Grouping = true;
            col.SubGrouping = false;
            this.SubGroupingDisabled = false;
        } else {
            this.Columns.forEach(c => c.SubGrouping = false);
            this.SubGroupingDisabled = true;
        }
    }

    subGroupingChanged(ev, col) {
        if (ev && ev.checked) {
            this.Columns.forEach(c => c.SubGrouping = false);
            col.SubGrouping = true;
        }
    }

    estimatorsChanged(ev) {
        if (ev && ev.value) {
            this.Data.ModelEstimators = ev.value;
        }
    }

    depthChanged(ev) {
        if (ev && ev.value) {
            this.Data.ModelDepth = ev.value;
        }
    }
}

export class MultiFeaturePredictionSettingsData extends MultiFeatureModelSettingsData {
    Steps = 50;
    ModelKey: string;
}

export class MultiFeaturePredictionSettingsDataHelper extends WorkflowModuleSettingsHelper {
    getExitPoints(settings: any): WorkflowExitInfo[] {
        const retVal = new WorkflowExitInfo();
        retVal.Type = 'relData';
        return [retVal];
    }
    getEntryPoints(): WorkflowExitInfo[] {
        const def = new WorkflowExitInfo();
        def.Type = 'relData';
        const fe = new WorkflowExitInfo();
        fe.ID = 1;
        fe.Label = '@@ForEach';
        fe.Type = 'forEach';
        return [def, fe];
    }
    getEmptySettingsInstance() {
        return new MultiFeaturePredictionSettingsData();
    }
    async fillActualState(module, state, wfData) {
        if (state) {
            const ds = state.get('DataSource');
            if (ds) {
                ds.Columns = [
                    {
                        Name: 'Category',
                        DataTyp: 'System.String'
                    },
                    {
                        Name: 'Subcategory',
                        DataTyp: 'System.String'
                    },
                    {
                        Name: 'Date',
                        DataTyp: 'System.DateTime'
                    },
                    {
                        Name: 'Predict',
                        DataTyp: 'System.Double'
                    },
                    {
                        Name: 'Accuracy',
                        DataTyp: 'System.Double'
                    }
                ];
            }
        }
    }
}
